/* ──────────────────────────────────────────────────────────────
   ClienteEstadoLogTab
   
   Pestaña para mostrar el histórico de cambios de estado de un cliente
   ────────────────────────────────────────────────────────────── */
import React from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useClienteEstadoLog } from '../hooks/useClientes';
import { MotivoDesactivacion } from '../types/usuarios';

interface Props {
  clienteId: number;
}

const MOTIVO_LABELS: Record<MotivoDesactivacion, string> = {
  [MotivoDesactivacion.BAJA]: 'Baja del servicio',
  [MotivoDesactivacion.NO_MAS_CLASE]: 'Sin clases programadas',
  [MotivoDesactivacion.ERROR_CREACION]: 'Error en la creación',
};

const MOTIVO_COLORS: Record<MotivoDesactivacion, 'error' | 'warning' | 'default'> = {
  [MotivoDesactivacion.BAJA]: 'error',
  [MotivoDesactivacion.NO_MAS_CLASE]: 'warning',
  [MotivoDesactivacion.ERROR_CREACION]: 'default',
};

export default function ClienteEstadoLogTab({ clienteId }: Props) {
  const { data: historico, isLoading, error } = useClienteEstadoLog(clienteId);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error al cargar el histórico: {error.message}
      </Alert>
    );
  }

  if (!historico || historico.length === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No hay cambios de estado registrados para este cliente
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Histórico de Cambios de Estado
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Registro completo de activaciones y desactivaciones
      </Typography>

      <Stack spacing={2}>
        {historico.map((log, index) => {
          const isFirst = index === 0;
          const fecha = parseISO(log.createdAt);

          return (
            <Paper
              key={log.id}
              elevation={isFirst ? 3 : 1}
              sx={{
                p: 2,
                borderLeft: `4px solid ${log.activo ? '#4caf50' : '#f44336'}`,
                position: 'relative',
              }}
            >
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                {log.activo ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <CancelIcon color="error" />
                )}
                <Box flex={1}>
                  <Typography
                    variant="subtitle1"
                    component="div"
                    fontWeight={isFirst ? 'bold' : 'normal'}
                  >
                    {log.activo ? 'Cliente Activado' : 'Cliente Desactivado'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {format(fecha, "d 'de' MMMM 'a las' HH:mm", { locale: es })}
                  </Typography>
                </Box>
                {isFirst && (
                  <Chip label="Más reciente" size="small" color="primary" />
                )}
              </Box>

              {!log.activo && log.deactivationReason && (
                <Chip
                  label={MOTIVO_LABELS[log.deactivationReason]}
                  size="small"
                  color={MOTIVO_COLORS[log.deactivationReason]}
                  sx={{ mb: 1 }}
                />
              )}

              <Divider sx={{ my: 1 }} />

              <Box display="flex" alignItems="center" gap={0.5}>
                {log.actorId ? (
                  <>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      Por: Usuario #{log.actorId}
                    </Typography>
                  </>
                ) : (
                  <>
                    <ComputerIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      Acción automática del sistema
                    </Typography>
                  </>
                )}
              </Box>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}
